import { useEffect, useMemo, useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchOrganisationen, type Organisation } from "@/lib/api"
import { EmptyState } from "@/components/EmptyState"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import { Sheet, SheetContent, SHEET_WIDTH } from "@/components/ui/sheet"
import { OrganisationDetailContent } from "@/pages/OrganisationDetailPage"
import { BUTTON_PRIMARY_CLASS, Input, Select } from "@/components/ui"

const TYP_LABELS: Record<string, string> = {
  kunde: "Kunde",
  lieferant: "Lieferant",
  behoerde: "Behörde",
  dienstleister: "Dienstleister",
}

const TYP_OPTIONEN = Object.entries(TYP_LABELS)

/**
 * Organisationen-Liste als DataGrid (Sortierung/Filter/Spaltenauswahl) mit
 * Detail im Rechts-Sheet — AP-2.2. `/organisationen/:id` bleibt als
 * Deep-Link-Fallback (OrganisationDetailPage).
 */
export function OrganisationenPage() {
  useEffect(() => {
    document.title = "Organisationen — RMNC Stammdaten"
  }, [])

  const queryClient = useQueryClient()
  const [suche, setSuche] = useState("")
  const [typ, setTyp] = useState("")
  const [ort, setOrt] = useState("")
  const [detailId, setDetailId] = useState<string | "neu" | null>(null)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["organisationen", { suche, typ, ort }],
    queryFn: () =>
      fetchOrganisationen({
        suche: suche || undefined,
        typ: typ || undefined,
        ort: ort || undefined,
        size: 100,
      }),
  })

  const sheetSchliessen = () => setDetailId(null)

  const spalten = useMemo<ColumnDef<Organisation>[]>(
    () => [
      {
        id: "name",
        header: "Name",
        accessorFn: (o) => o.name,
        cell: ({ row }) => row.original.name,
        meta: { cellClassName: "font-medium text-slate-900" },
      },
      {
        id: "name2",
        header: "Name 2",
        accessorFn: (o) => o.name2 ?? "",
        cell: ({ row }) => row.original.name2 || "—",
      },
      {
        id: "adresse",
        header: "Adresse",
        accessorFn: (o) => o.adresse ?? "",
        cell: ({ row }) => row.original.adresse || "—",
      },
      {
        id: "rufnummer",
        header: "Rufnummer",
        accessorFn: (o) => o.rufnummer ?? "",
        cell: ({ row }) => row.original.rufnummer || "—",
        meta: { cellClassName: "whitespace-nowrap" },
      },
      {
        id: "email",
        header: "E-Mail",
        accessorFn: (o) => o.email ?? "",
        cell: ({ row }) => row.original.email || "—",
      },
    ],
    [],
  )

  return (
    <div>
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Organisationen</h1>
          <p className="text-sm text-slate-600">
            Stammdaten-Hub (Ninox-08) — Kunden, Lieferanten, Behörden, Dienstleister.
          </p>
        </div>
        <button type="button" className={BUTTON_PRIMARY_CLASS} onClick={() => setDetailId("neu")}>
          + Neue Organisation
        </button>
      </header>

      <div className="mb-4 flex flex-wrap gap-3">
        <Input
          type="text"
          placeholder="Suche nach Name/Kürzel…"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          className="w-64"
        />
        <Select value={typ} onChange={(e) => setTyp(e.target.value)} className="w-auto">
          <option value="">Alle Typen</option>
          {TYP_OPTIONEN.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </Select>
        <Input
          type="text"
          placeholder="Ort…"
          value={ort}
          onChange={(e) => setOrt(e.target.value)}
          className="w-40"
        />
      </div>

      {isError && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>Organisationen konnten nicht geladen werden.</span>
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      <Sheet open={detailId !== null} onOpenChange={(open) => !open && sheetSchliessen()}>
        <SheetContent
          className="overflow-y-auto"
          sheetKey="organisation-detail"
          defaultWidth={SHEET_WIDTH.xl}
        >
          {detailId !== null && (
            <OrganisationDetailContent
              organisationId={detailId === "neu" ? null : detailId}
              onGespeichert={(org) => {
                queryClient.invalidateQueries({ queryKey: ["organisationen"] })
                setDetailId(org.id)
              }}
              onGeloescht={sheetSchliessen}
            />
          )}
        </SheetContent>
      </Sheet>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Organisationen …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}

      {data && (
        <DataGrid
          columns={spalten}
          data={data.data}
          storageKey="organisationen"
          initialSorting={[{ id: "name", desc: false }]}
          enableQuickFilter={false}
          onRowClick={(o) => setDetailId(o.id)}
          emptyMessage={
            suche || typ || ort
              ? "Keine Organisationen gefunden."
              : "Noch keine Organisationen — + Neue Organisation"
          }
        />
      )}

      {!isLoading && !isError && data && data.data.length === 0 && !suche && !typ && !ort && (
        <EmptyState text="Noch keine Organisationen — + Neue Organisation" />
      )}
    </div>
  )
}
